/**
 * @file pages/Home.js
 * @description Page d'accueil avec hero section
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const heroRef = useRef(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      setTimeout(() => {
        el.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 100);
    }
  }, []);

  return (
    <>
      <style>{`
        .home-hero {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 120px 24px 80px;
          position: relative;
          overflow: hidden;
        }
        .hero-bg {
          position: absolute;
          inset: 0;
          background: 
            radial-gradient(ellipse 80% 60% at 50% -20%, rgba(169, 134, 103, 0.15) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 80% 80%, rgba(169, 134, 103, 0.08) 0%, transparent 50%),
            linear-gradient(180deg, #1F2A44 0%, #141d30 100%);
        }
        .hero-ornament {
          position: absolute;
          width: 500px;
          height: 500px;
          border: 1px solid rgba(169, 134, 103, 0.08);
          border-radius: 50%;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .hero-ornament:nth-child(2) {
          width: 700px;
          height: 700px;
          border-color: rgba(169, 134, 103, 0.05);
          animation: slowSpin 40s linear infinite;
        }
        @keyframes slowSpin {
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 800px;
        }
        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(169, 134, 103, 0.15);
          border: 1px solid rgba(169, 134, 103, 0.3);
          color: #A98667;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          margin-bottom: 32px;
          animation: fadeInUp 0.6s ease 0.2s both;
        }
        .hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(3rem, 8vw, 5.5rem);
          font-weight: 700;
          color: #FFFFFF;
          line-height: 1.1;
          margin-bottom: 24px;
          animation: fadeInUp 0.6s ease 0.3s both;
        }
        .hero-title em {
          font-style: italic;
          color: #A98667;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-subtitle {
          font-size: 1.15rem;
          color: rgba(240, 234, 226, 0.7);
          line-height: 1.8;
          max-width: 560px;
          margin: 0 auto 48px;
          animation: fadeInUp 0.6s ease 0.4s both;
        }
        .hero-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          animation: fadeInUp 0.6s ease 0.5s both;
        }
        .hero-verse {
          position: absolute;
          bottom: 48px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-size: 1rem;
          color: rgba(169, 134, 103, 0.6);
          white-space: nowrap;
          z-index: 2;
        }
        /* Features section */
        .features {
          padding: 100px 24px;
          background: #141d30;
        }
        .features-grid {
          max-width: 1100px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
          margin-top: 64px;
        }
        .feature-card {
          background: rgba(31, 42, 68, 0.6);
          border: 1px solid rgba(169, 134, 103, 0.15);
          border-radius: 16px;
          padding: 36px 32px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #A98667, transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .feature-card:hover {
          border-color: rgba(169, 134, 103, 0.35);
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.25);
        }
        .feature-card:hover::before {
          opacity: 1;
        }
        .feature-icon {
          width: 56px;
          height: 56px;
          background: rgba(169, 134, 103, 0.15);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin-bottom: 20px;
        }
        .feature-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.4rem;
          color: #FFFFFF;
          margin-bottom: 12px;
        }
        .feature-desc {
          color: rgba(240, 234, 226, 0.6);
          font-size: 0.95rem;
          line-height: 1.7;
        }
        /* Section titre */
        .section-label {
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #A98667;
          margin-bottom: 16px;
          text-align: center;
        }
        .section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2rem, 4vw, 3rem);
          color: #FFFFFF;
          text-align: center;
          line-height: 1.2;
        }
        /* CTA section */
        .cta-section {
          padding: 100px 24px;
          text-align: center;
          background: linear-gradient(135deg, #1F2A44, #2a3a5c);
          position: relative;
          overflow: hidden;
        }
        .cta-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 60% 80% at 50% 50%, rgba(169, 134, 103, 0.12), transparent 70%);
        }
        .cta-section .cta-content {
          position: relative;
          z-index: 1;
          max-width: 600px;
          margin: 0 auto;
        }
        .cta-section h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2rem, 4vw, 3rem);
          color: #FFFFFF;
          margin-bottom: 16px;
        }
        .cta-section p {
          color: rgba(240, 234, 226, 0.7);
          margin-bottom: 40px;
          font-size: 1.05rem;
        }
        @media (max-width: 600px) {
          .hero-actions { flex-direction: column; align-items: center; }
          .hero-verse { display: none; }
        }
      `}</style>

      {/* ── Hero ── */}
      <section className="home-hero">
        <div className="hero-bg" />
        <div className="hero-ornament" />
        <div className="hero-ornament" />

        <div className="hero-content" ref={heroRef}>
          <div className="hero-badge">
            ✦ Étude Biblique Guidée
          </div>
          <h1 className="hero-title">
            Plongez dans la<br /><em>Parole Vivante</em>
          </h1>
          <p className="hero-subtitle">
            Un espace de méditation, d'apprentissage et de découverte spirituelle. 
            Explorez les Écritures à votre rythme, notez vos réflexions et testez votre connaissance.
          </p>
          <div className="hero-actions">
            <Link to="/moments" className="btn-primary">
              ✦ Commencer l'étude
            </Link>
            {!isAuthenticated && (
              <Link to="/register" className="btn-secondary">
                S'inscrire gratuitement
              </Link>
            )}
          </div>
        </div>

        <div className="hero-verse">
          « Ta parole est une lampe à mes pieds, et une lumière sur mon sentier. » — Psaumes 119:105
        </div>
      </section>

      {/* ── Features ── */}
      <section className="features">
        <div className="section-label">Ce que nous offrons</div>
        <h2 className="section-title">Un voyage spirituel<br />structuré et inspirant</h2>

        <div className="features-grid">
          {[
            {
              icon: '📖',
              title: 'Moments d\'Étude',
              desc: 'Des parcours thématiques soigneusement conçus pour approfondir votre compréhension de la Bible, livre par livre.'
            },
            {
              icon: '📅',
              title: 'Calendrier de Méditation',
              desc: 'Planifiez vos moments de prière et méditation. Recevez des rappels par email et notification pour ne jamais manquer un rendez-vous spirituel.'
            },
            {
              icon: '⚡',
              title: 'Quiz Interactif',
              desc: 'Testez et renforcez vos connaissances bibliques avec des questions chronométrées et un feedback immédiat.'
            },
            
          ].map((f, i) => (
            <div className="feature-card" key={i} style={{ animationDelay: `${i * 0.1}s`, cursor: i === 1 ? 'pointer' : 'default' }} onClick={i === 1 ? () => window.location.href = '/meditation' : undefined}>
              <div className="feature-icon">{f.icon}</div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Prêt à commencer<br />votre voyage ?</h2>
          <p>Rejoignez notre communauté et commencez votre étude biblique dès aujourd'hui.</p>
          {isAuthenticated ? (
            <Link to="/moments" className="btn-primary">Explorer les Moments</Link>
          ) : (
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" className="btn-primary">Créer mon compte</Link>
              <Link to="/login" className="btn-secondary">Se connecter</Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Home;